import React, {
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import { Scrollbars as LibraryScrollbars } from "react-custom-scrollbars-2";
import { useWindowSize } from "hooks/useWindowSize";
import { IsRunningOverwolf } from "utilityFunctions";

export interface ScrollbarsProps {
  scrollTopWhenChanged?: any;
  scrollBottomWhenChanged?: any;
  saveScrollValue?: boolean;
  style?: React.CSSProperties;
  className?: string;
  setScrollOnce?: {
    shouldSetScroll: boolean;
    setShouldSetScroll: (val: boolean) => void;
    scrollTopValue: number | null;
  };
  children?: React.ReactNode;
}

export const Scrollbars = forwardRef<LibraryScrollbars, ScrollbarsProps>(
  function ScrollbarsComponent(
    {
      scrollTopWhenChanged,
      scrollBottomWhenChanged,
      saveScrollValue,
      style,
      className,
      setScrollOnce,
      children,
    },
    ref
  ) {
    const [initialScrollFinished, setInitialScrollFinished] = useState(false);
    const [width] = useWindowSize();
    // Track whether the mouse is over the scrollable component.
    const [isHovered, setIsHovered] = useState(false);
    const scrollMultiplier = 0.8;
    // Ref to the underlying LibraryScrollbars instance.
    const internalRef = useRef<LibraryScrollbars>(null);

    // Expose the internal ref to parent components.
    useImperativeHandle(
      ref,
      () => internalRef.current as LibraryScrollbars,
      []
    );

    const handleSaveScroll = (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop } = event.currentTarget;
      sessionStorage.setItem("scrollbar.scrollTop", scrollTop.toFixed());
    };

    const renderTrack = () => {
      const trackStyle: React.CSSProperties = {
        height: "100%",
        width: "12px",
        top: 0,
        right: 0,
        borderRadius: "5px",
        padding: "5px",
        display: "flex",
        justifyContent: "center",
        position: "absolute",
      };
      return <div style={{ ...trackStyle, ...style }} />;
    };

    const renderThumb = ({ style: thumbStyle, ...props }: any) => {
      const dynamicThumbStyle: React.CSSProperties =
        width < 1024 && !IsRunningOverwolf()
          ? { position: "absolute", width: "5px", borderRadius: "5px" }
          : {
              position: "absolute",
              width: "5px",
              borderRadius: "5px",
              backgroundColor: "#cbd5e1",
            };
      return <div style={{ ...thumbStyle, ...dynamicThumbStyle }} {...props} />;
    };

    /**
     * onWheel handler:
     * - If the mouse is hovering over our scrollable component and its inner view
     *   is not completely in view, delegate the wheel event to the window.
     * - Otherwise, let the inner scroll behavior occur as normal.
     */
    const handleWheel = (e: React.WheelEvent<LibraryScrollbars>) => {
      // Only run custom logic when hovering over the component.
      if (!isHovered) {
        return;
      }
      if (!internalRef.current) return;

      // Get the inner DOM element. (Cast to any since "view" isn’t defined in the type.)
      const container =
        (internalRef.current as any).getView?.() ||
        (internalRef.current as any).view;
      if (!container) return;

      // Check if the container is fully visible.
      const rect = container.getBoundingClientRect();
      const isFullyVisible = rect.top >= 0 && rect.bottom <= window.innerHeight;

      if (!isFullyVisible) {
        // Delegate the scroll to the window.
        // Note: e.deltaY is negative for upward scrolling and positive for downward.
        window.scrollBy({ top: e.deltaY * scrollMultiplier, behavior: "auto" });
        // Prevent the inner scroll from also handling this event.
        e.preventDefault();
      }
      // If the container is fully visible, do nothing here so that the inner scroll works.
    };

    useEffect(() => {
      if (!internalRef.current) return;
      const initialScroll = sessionStorage.getItem("scrollbar.scrollTop");
      if (!initialScroll) {
        const scrollHeight = internalRef.current.getScrollHeight();
        sessionStorage.setItem("scrollbar.scrollTop", scrollHeight.toString());
      }
    }, []);

    useEffect(() => {
      if (!internalRef.current) return;
      if (scrollTopWhenChanged) {
        internalRef.current.scrollTop(0);
      }
      if (scrollBottomWhenChanged && initialScrollFinished) {
        internalRef.current.scrollToBottom();
      }
      setInitialScrollFinished(true);
    }, [scrollTopWhenChanged, scrollBottomWhenChanged, initialScrollFinished]);

    return (
      <LibraryScrollbars
        ref={internalRef}
        className={className}
        style={style}
        onWheel={handleWheel}
        onScroll={saveScrollValue ? handleSaveScroll : undefined}
        renderTrackVertical={renderTrack}
        renderThumbVertical={renderThumb}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onLoad={() => {
          if (
            setScrollOnce &&
            setScrollOnce.shouldSetScroll &&
            internalRef.current
          ) {
            const top = setScrollOnce.scrollTopValue ?? 0;
            internalRef.current.scrollTop(top);
            setScrollOnce.setShouldSetScroll(false);
          }
        }}
      >
        <div className="pb-6 overflow-x-hidden hide-scrollbar">{children}</div>
      </LibraryScrollbars>
    );
  }
);
